import React, { useEffect, useState } from "react";
import { useWebData } from "../context/WebDataContext"; 
import { db } from "../firebaseConfig";
import { doc, deleteDoc } from "firebase/firestore"; 
import * as XLSX from "xlsx";
import Loader from "../components/Loader";

function ServiceReports() {
  // 🔥 FETCH EVERYTHING (PMS, Install, Service Calls, Demos)
  const { pms, installations, serviceCalls, demos, serviceReports, loading } = useWebData(); 

  // 🔥 Filter States
  const [viewMode, setViewMode] = useState('Month'); 
  const [selectedDate, setSelectedDate] = useState(new Date()); 
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear()); 
  const [reportType, setReportType] = useState('All'); 

  const [searchTerm, setSearchTerm] = useState("");
  const [filtered, setFiltered] = useState([]);

  // States
  const [viewData, setViewData] = useState(null);

  // ==========================================
  // 1. DATA MERGING (Consolidating 4 Sources)
  // ==========================================
  useEffect(() => {
    let allData = [];

    // 1. PMS
    if (pms) {
        allData = [...allData, ...pms.map(i => ({ 
            ...i, 
            reportType: 'PMS', 
            // Priority: Done Date -> Next Due -> Scheduled
            displayDate: i.status === 'Done' ? (i.date || i.createdAt) : (i.nextServiceDate || i.scheduledDate),
            details: `Cycle: ${i.currentPmsNumber}/${i.totalPms}`,
            hospital: i.hospital
        }))];
    }

    // 2. Installations
    if (installations) {
        allData = [...allData, ...installations.map(i => ({ 
            ...i, 
            reportType: 'Installation', 
            displayDate: i.date,
            details: `Model: ${i.model}`,
            hospital: i.hospital
        }))];
    }

    // 3. Service Calls (Treating as BREAKDOWN)
    if (serviceCalls) {
        allData = [...allData, ...serviceCalls.map(i => ({ 
            ...i, 
            reportType: 'Breakdown', 
            displayDate: i.createdAt, // Ticket Open Date
            hospital: i.hospitalName, // Service Call uses 'hospitalName'
            engineer: i.senderName || i.userName, // Adjust field
            details: i.remark, // Problem description
            status: i.status
        }))];
    }

    // 4. Demos (🔥 NEW)
    if (demos) {
        allData = [...allData, ...demos.map(i => ({
            ...i,
            reportType: 'Demo',
            displayDate: i.date,
            hospital: i.hospital,
            details: `Result: ${i.result || 'Pending'}`,
            status: i.status
        }))];
    }

    // --- FILTERS ---

    // A. Type Filter
    if (reportType !== 'All') {
        allData = allData.filter(i => i.reportType === reportType);
    }

    // B. Search Filter
    if (searchTerm) {
        const lower = searchTerm.toLowerCase();
        allData = allData.filter(i => {
            const dateStr = formatDate(i.displayDate).toLowerCase();
            const row = `${dateStr} ${i.hospital} ${i.userName} ${i.engineer} ${i.status} ${i.reportType} ${i.serialNo} ${i.details}`.toLowerCase();
            return row.includes(lower);
        });
    }

    // C. Date Filter
    if (viewMode !== 'All') {
        const targetYear = viewMode === 'Year' ? parseInt(selectedYear) : selectedDate.getFullYear();
        const targetMonth = selectedDate.getMonth(); 

        allData = allData.filter(item => {
            const rawDate = item.displayDate || ""; 
            if (!rawDate) return false;
            let y = 0; let m = -1;
            const d = new Date(rawDate);
            if (!isNaN(d.getTime())) { y = d.getFullYear(); m = d.getMonth(); } 
            else {
                const parts = rawDate.split(/[-/]/);
                if (parts.length === 3) {
                    if (parts[2].length === 4) { y = parseInt(parts[2]); m = parseInt(parts[1]) - 1; } 
                    else if (parts[0].length === 4) { y = parseInt(parts[0]); m = parseInt(parts[1]) - 1; }
                }
            }
            if (viewMode === 'Year') return y === targetYear;
            if (viewMode === 'Month') return y === targetYear && m === targetMonth;
            return true;
        });
    }

    // Sort (Newest First)
    allData.sort((a, b) => new Date(b.displayDate || 0) - new Date(a.displayDate || 0));
    setFiltered(allData);

  }, [pms, installations, serviceCalls, demos, serviceReports, searchTerm, viewMode, selectedDate, selectedYear, reportType]); 

  // ==========================================
  // 2. EXCEL DOWNLOAD
  // ==========================================
  const downloadExcel = () => {
    const dataToExport = filtered.map(r => ({
        Date: formatDate(r.displayDate),
        Type: r.reportType,
        Hospital: r.hospital,
        City: r.city || "-",
        Engineer: r.userName || r.engineer,
        Details: r.details || "-",
        Status: r.status,
        Serial_No: r.serialNo || "-"
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Master_Report");
    XLSX.writeFile(wb, `Master_Report_${viewMode}.xlsx`);
  };

  const handleDelete = async (row) => {
      if(window.confirm("Delete this record?")) {
          // Identify Collection based on Type
          let collectionName = "service_reports";
          if(row.reportType === 'PMS') collectionName = "pms_reports";
          if(row.reportType === 'Installation') collectionName = "installations";
          if(row.reportType === 'Breakdown') collectionName = "service_calls";
          if(row.reportType === 'Demo') collectionName = "demo_notes";
          
          await deleteDoc(doc(db, collectionName, row.id));
      }
  };

  // Helpers
  const handleMonthChange = (dir) => {
      const d = new Date(selectedDate);
      d.setMonth(d.getMonth() + dir);
      setSelectedDate(d);
  };

  const getYearOptions = () => {
      const currentY = new Date().getFullYear();
      const years = [];
      for(let y = 2024; y <= currentY + 5; y++) years.push(y);
      return years;
  };

  const formatDate = (dateStr) => {
      if(!dateStr) return "-";
      const date = new Date(dateStr);
      return isNaN(date.getTime()) ? dateStr : date.toLocaleDateString('en-GB');
  };

  return (
    <div style={{padding:"20px", fontFamily:"'Segoe UI', sans-serif", backgroundColor:"#f8f9fa", minHeight:"100vh"}}>
      
      {/* HEADER */}
      <div style={headerCard}>
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"15px"}}>
            <h2 style={{margin:0, color:"#c0392b", display:"flex", alignItems:"center"}}>
                🛠️ Consolidated Reports <span style={{fontSize:"14px", color:"#777", marginLeft:"10px"}}>({filtered.length})</span>
            </h2>
            <button onClick={downloadExcel} style={btnDownload}>📥 Download All</button>
        </div>

        {/* 🔥 REPORT TYPE TABS (Added Demo) */}
        <div style={{display:"flex", gap:"10px", marginBottom:"15px", borderBottom:"1px solid #eee", paddingBottom:"10px", overflowX:"auto"}}>
            {['All', 'PMS', 'Installation', 'Breakdown', 'Demo'].map(type => (
                <button key={type} onClick={()=>setReportType(type)} 
                    style={{
                        padding:"8px 20px", borderRadius:"20px", border:"none", cursor:"pointer", fontWeight:"bold", fontSize:"13px", whiteSpace:"nowrap",
                        backgroundColor: reportType===type ? getColor(type) : "#eee",
                        color: reportType===type ? "white" : "#555",
                        boxShadow: reportType===type ? "0 2px 5px rgba(0,0,0,0.2)" : "none",
                        transition: "0.2s"
                    }}>
                    {type}
                </button>
            ))}
        </div>

        {/* DATE & SEARCH */}
        <div style={{display:"flex", gap:"10px", flexWrap:"wrap", alignItems:"center", justifyContent:"space-between"}}>
            <div style={{display:"flex", gap:"5px", backgroundColor:"#eee", padding:"4px", borderRadius:"6px"}}>
                {['Month', 'Year', 'All'].map(mode => (
                    <button key={mode} onClick={()=>setViewMode(mode)} 
                        style={{...toggleBtn, backgroundColor: viewMode===mode ? "white" : "transparent", color: viewMode===mode ? "#333" : "#777", boxShadow: viewMode===mode ? "0 2px 5px rgba(0,0,0,0.1)" : "none"}}>
                        {mode}
                    </button>
                ))}
            </div>

            {viewMode === 'Month' && (
                <div style={{display:"flex", alignItems:"center", gap:"10px", backgroundColor:"white", padding:"5px 15px", borderRadius:"6px", border:"1px solid #ddd"}}>
                    <button onClick={()=>handleMonthChange(-1)} style={navBtn}>◀</button>
                    <span style={{fontWeight:"bold", color:"#333", minWidth:"120px", textAlign:"center"}}>
                        {selectedDate.toLocaleDateString('en-US', {month:'long', year:'numeric'})}
                    </span>
                    <button onClick={()=>handleMonthChange(1)} style={navBtn}>▶</button>
                </div>
            )}

            {viewMode === 'Year' && (
                <select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} style={{padding:"8px", borderRadius:"6px", border:"1px solid #ddd", fontWeight:"bold", fontSize:"14px", cursor:"pointer"}}>
                    {getYearOptions().map(y => <option key={y} value={y}>{y}</option>)}
                </select>
            )}

            <input type="text" placeholder="🔍 Search Hospital, Serial, Type..." value={searchTerm} onChange={(e)=>setSearchTerm(e.target.value)} style={searchInput} />
        </div>
      </div>

      {loading ? <Loader /> : (
        <div style={{overflowX:"auto", backgroundColor:"white", borderRadius:"8px", boxShadow:"0 2px 10px rgba(0,0,0,0.05)"}}>
          <table style={tableStyle}>
            <thead>
                <tr style={{backgroundColor:"#34495e", color:"white", textAlign:"left"}}>
                    <th style={thStyle}>Date</th>
                    <th style={thStyle}>Type</th>
                    <th style={thStyle}>Hospital</th>
                    <th style={thStyle}>Engineer</th>
                    <th style={thStyle}>Key Info</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Actions</th>
                </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                  <tr><td colSpan="7" style={{padding:"20px", textAlign:"center", color:"#999"}}>No reports found.</td></tr>
              ) : filtered.map((r, idx) => (
                <tr key={r.id || idx} style={{borderBottom:"1px solid #eee", height:"45px"}}>
                    <td style={tdStyle}>{formatDate(r.displayDate)}</td>
                    
                    <td style={tdStyle}>
                        <span style={{
                            padding:"3px 8px", borderRadius:"4px", fontSize:"11px", fontWeight:"bold", color:"white",
                            backgroundColor: getColor(r.reportType)
                        }}>
                            {r.reportType}
                        </span>
                    </td>

                    <td style={{...tdStyle, textAlign:"left"}}>
                        <div style={{fontWeight:"bold"}}>{r.hospital}</div>
                        <div style={{fontSize:"10px", color:"gray"}}>{r.city || '-'}</div>
                    </td>

                    <td style={tdStyle}>{r.userName || r.engineer}</td>

                    <td style={{...tdStyle, fontSize:"11px", maxWidth:"200px"}}>
                        <div style={{fontWeight:"bold", color:"#555"}}>{r.machine || r.product}</div>
                        <div style={{color:"#777"}}>{r.details ? r.details.substring(0, 30) : "-"}</div>
                    </td>

                    <td style={{...tdStyle, color: r.status==='Done' || r.status==='Completed' || r.status==='Closed' || r.status==='Successful' ?'green':'orange', fontWeight:"bold"}}>{r.status}</td>
                    
                    <td style={tdStyle}>
                        <div style={{display:"flex", gap:"3px"}}>
                            <button onClick={() => setViewData(r)} style={btnIcon} title="View Details">👁️</button>
                            <button onClick={() => handleDelete(r)} style={btnIconDelete} title="Delete">🗑️</button>
                        </div>
                    </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* VIEW MODAL (Adaptive based on Type) */}
      {viewData && (
        <div style={modalOverlay}>
          <div style={modalContent}>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"15px", borderBottom:"1px solid #eee", paddingBottom:"10px"}}>
                <h3 style={{margin:0, color: getColor(viewData.reportType)}}>{viewData.reportType} Details</h3>
                <button onClick={() => setViewData(null)} style={{background:"none", border:"none", fontSize:"18px", cursor:"pointer"}}>✖</button>
            </div>
            
            <div style={{fontSize:"13px", lineHeight:"1.6"}}>
                <div style={{display:"flex", justifyContent:"space-between"}}>
                    <strong>Date: {formatDate(viewData.displayDate)}</strong>
                    <strong>{viewData.ticketNo ? `Ticket: ${viewData.ticketNo}` : ""}</strong>
                </div>
                <hr style={{border:"0.5px solid #eee"}}/>
                
                <p><strong>🏥 Hospital:</strong> {viewData.hospital}, {viewData.city}</p>
                <p><strong>👷 Engineer:</strong> {viewData.userName || viewData.engineer}</p>
                <p><strong>⚙️ Machine:</strong> {viewData.machine || viewData.product}</p>
                <p><strong>🔢 Serial:</strong> {viewData.serialNo}</p>

                {/* CONDITIONAL FIELDS */}
                {viewData.reportType === 'PMS' && (
                    <div style={{background:"#f3e5f5", padding:"10px", borderRadius:"5px", marginTop:"10px"}}>
                        <p style={{margin:0, color:"#8e44ad", fontWeight:"bold"}}>🔄 PMS Cycle</p>
                        <p style={{margin:0}}>Current: {viewData.currentPmsNumber} | Total: {viewData.totalPms}</p>
                        <p style={{margin:"5px 0 0 0"}}><strong>Next Due:</strong> {formatDate(viewData.nextServiceDate)}</p>
                    </div>
                )}

                {viewData.reportType === 'Installation' && (
                    <div style={{background:"#e8f5e9", padding:"10px", borderRadius:"5px", marginTop:"10px"}}>
                        <p style={{margin:0, color:"#27ae60", fontWeight:"bold"}}>⚙️ Installation Data</p>
                        <p style={{margin:0}}>Warranty Expiry: {viewData.warrantyExpiry}</p>
                        <p style={{margin:0}}>Department: {viewData.department}</p>
                    </div>
                )}

                {viewData.reportType === 'Breakdown' && (
                    <div style={{background:"#ffebee", padding:"10px", borderRadius:"5px", marginTop:"10px"}}>
                        <p style={{margin:0, color:"#c0392b", fontWeight:"bold"}}>⚠️ Breakdown / Service Call</p>
                        <p style={{margin:0}}><strong>Issue:</strong> {viewData.remark || viewData.issue}</p>
                        <p style={{margin:"5px 0 0 0"}}><strong>Action:</strong> {viewData.resolutionNote || viewData.action}</p>
                    </div>
                )}

                {viewData.reportType === 'Demo' && (
                    <div style={{background:"#e0f7fa", padding:"10px", borderRadius:"5px", marginTop:"10px"}}>
                        <p style={{margin:0, color:"#0097a7", fontWeight:"bold"}}>🎭 Demo Info</p>
                        <p style={{margin:0}}><strong>Given To:</strong> {viewData.contactPerson}</p>
                        <p style={{margin:"5px 0 0 0"}}><strong>Result:</strong> {viewData.result}</p>
                    </div>
                )}

                <p style={{marginTop:"10px", textAlign:"right"}}><strong>🔥 Status:</strong> {viewData.status}</p>
                {viewData.remarks && <p><strong>📝 Remarks:</strong> {viewData.remarks}</p>}
            </div>
            <button onClick={() => setViewData(null)} style={btnClose}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

// Helpers
const getColor = (type) => {
    switch(type) {
        case 'PMS': return '#8e44ad'; // Purple
        case 'Installation': return '#27ae60'; // Green
        case 'Breakdown': return '#c0392b'; // Red
        case 'Demo': return '#00acc1'; // Cyan
        default: return '#333';
    }
};

// STYLES
const headerCard={backgroundColor:"white", padding:"20px", borderRadius:"10px", boxShadow:"0 2px 8px rgba(0,0,0,0.05)", marginBottom:"20px"};
const tableStyle={width:"100%", borderCollapse:"collapse", fontSize:"13px"};
const thStyle={padding:"12px 15px", borderBottom:"2px solid #eee", fontSize:"12px", textTransform:"uppercase", letterSpacing:"0.5px"};
const tdStyle={padding:"10px 15px", verticalAlign:"middle"};
const toggleBtn={border:"none", padding:"6px 12px", borderRadius:"5px", cursor:"pointer", fontSize:"13px", fontWeight:"600", transition:"0.2s"};
const navBtn={background:"none", border:"none", cursor:"pointer", fontSize:"16px", color:"#555"};
const searchInput={padding:"8px 12px", border:"1px solid #ddd", borderRadius:"6px", width:"200px", outline:"none"};
const btnDownload={backgroundColor:"#27ae60", color:"white", border:"none", padding:"8px 15px", borderRadius:"5px", cursor:"pointer", fontWeight:"bold", fontSize:"13px"};

const btnIcon={backgroundColor:"#3498db", color:"white", border:"none", padding:"5px 8px", borderRadius:"4px", cursor:"pointer", marginRight:"5px"};
const btnIconDelete={backgroundColor:"#fff", color:"#777", border:"1px solid #ddd", padding:"5px 8px", borderRadius:"4px", cursor:"pointer"};

const modalOverlay={position:"fixed", top:0, left:0, width:"100%", height:"100%", background:"rgba(0,0,0,0.5)", display:"flex", justifyContent:"center", alignItems:"center", zIndex:1000};
const modalContent={background:"white", padding:"25px", borderRadius:"12px", width:"400px", boxShadow:"0 10px 30px rgba(0,0,0,0.2)", position:"relative"};
const btnClose={marginTop:"15px", backgroundColor:"#333", color:"white", border:"none", padding:"8px", borderRadius:"4px", width:"100%", cursor:"pointer"};

export default ServiceReports;